import { formatLocaleCurrency, formatLocaleDate, formatLocaleNumber } from "@/utils/formatUtils";
import { tBoolean } from "@/utils/translationUtils";
import { isDefined } from "@/utils/utils";
import { Descriptions } from "antd";
import { useTranslation } from "react-i18next";
import { ClientLineDescription } from "../../../../../client/components/ClientLineDescription";
import { ClientFormType } from "../../../../../client/enums";
import { GenericContract } from "../../../../types";
import { ContractAgentsData } from "../../../ContractAgentsData";
import { ContractClientsData } from "../../../ContractClientsData";
import { createCancellationDateDescriptionItem } from "../../../description-items/cancellationDateDescriptionItem";
import { createNoteDescriptionItem } from "../../../description-items/noteDescriptionItem";

interface Props {
  contract: GenericContract;
}

export const GenericContractDataSection = ({ contract }: Props) => {
  const { t } = useTranslation();

  const client = contract.clients[contract.policyHolderIndex];

  return (
    <Descriptions
      bordered
      column={1}
      styles={{ label: { width: 180, fontWeight: "bold" } }}
      items={[
        {
          label: t("contract.sections.clients"),
          children: client && (
            <ContractClientsData
              items={[
                {
                  label: t(`client.enums.formType.${ClientFormType.POLICY_HOLDER}`),
                  children: <ClientLineDescription client={client} />,
                },
              ]}
            />
          ),
        },
        {
          label: t("contract.sections.contractData"),
          children: (
            <Descriptions
              bordered
              size="small"
              column={3}
              items={[
                {
                  label: t("contract.attrs.mediationReportSignDate"),
                  children: contract.mediationReportSignDate
                    ? formatLocaleDate(contract.mediationReportSignDate)
                    : undefined,
                },
                {
                  label: t("contract.attrs.signDate"),
                  children: formatLocaleDate(contract.signDate),
                },
                createCancellationDateDescriptionItem(contract),
                {
                  label: t("contract.attrs.tradeAmount"),
                  children: formatLocaleCurrency(contract.tradeAmount),
                },
                {
                  label: t("contract.attrs.regularPayment"),
                  children: isDefined(contract.regularPayment)
                    ? formatLocaleCurrency(contract.regularPayment)
                    : undefined,
                },
                {
                  label: t("contract.attrs.feeTotalAmount"),
                  children: isDefined(contract.feeTotalAmount)
                    ? formatLocaleCurrency(contract.feeTotalAmount)
                    : undefined,
                },
                {
                  label: t("contract.attrs.transferredFromOtherBroker"),
                  children: tBoolean(contract.transferredFromOtherBroker),
                },
                {
                  label: t("contract.attrs.transferredToOtherBroker"),
                  children: contract.transferredToOtherBrokerDate
                    ? formatLocaleDate(contract.transferredToOtherBrokerDate)
                    : undefined,
                  span: 2,
                },
                {
                  label: t("contract.attrs.commodities"),
                  children: contract.commodities ? (
                    <Descriptions
                      size="small"
                      bordered
                      column={1}
                      items={[
                        {
                          label: t("contract.enums.commodityPaymentType._label"),
                          children: contract.commodityPaymentType
                            ? t(`contract.enums.commodityPaymentType.${contract.commodityPaymentType}`)
                            : undefined,
                        },
                        {
                          label: t("contract.enums.commodityPaymentFrequency._label"),
                          children: contract.commodityPaymentFrequency
                            ? t(`contract.enums.commodityPaymentFrequency.${contract.commodityPaymentFrequency}`)
                            : undefined,
                        },
                        {
                          label: t("contract.attrs.commodityPiecesCount"),
                          children: isDefined(contract.commodityPiecesCount)
                            ? `${formatLocaleNumber(contract.commodityPiecesCount)} ${t("common.sign.piece")}`
                            : undefined,
                        },
                        {
                          label: t("contract.attrs.commodityWeight"),
                          children: isDefined(contract.commodityWeight)
                            ? `${formatLocaleNumber(contract.commodityWeight, 2)} ${t("common.sign.g")}`
                            : undefined,
                        },
                        {
                          label: t("contract.enums.commodityCurrency._label"),
                          children: contract.commodityCurrency
                            ? t(`contract.enums.commodityCurrency.${contract.commodityCurrency}`)
                            : undefined,
                        },
                        {
                          label: t("contract.enums.commodityShape._label"),
                          children: contract.commodityShape
                            ? t(`contract.enums.commodityShape.${contract.commodityShape}`)
                            : undefined,
                        },
                        {
                          label: t("contract.enums.commoditySavingsPlan._label"),
                          children: contract.commoditySavingsPlan
                            ? t(`contract.enums.commoditySavingsPlan.${contract.commoditySavingsPlan}`)
                            : undefined,
                        },
                      ]}
                    />
                  ) : (
                    tBoolean(contract.commodities)
                  ),
                  span: 3,
                },
                createNoteDescriptionItem(contract),
              ]}
            />
          ),
        },
        {
          label: t("contract.sections.agents"),
          children: <ContractAgentsData contract={contract} />,
        },
      ]}
    />
  );
};
